// 示例方法，没有实际意义

import dayjs from 'dayjs';
import * as math from 'mathjs';

export function trim(str: string) {
  return str.trim();
}

/**
 * 加密铭文密码
 * @param password
 */
export function encodeWebPassword(password: string) {
  let encodedPassword = '';
  for (let i = 0; i < password.length; i++) {
    let charCode = password.charCodeAt(i);
    encodedPassword += String.fromCharCode(charCode ^ 127);
  }
  return btoa(encodedPassword);
}
type EmptyValue =
  | undefined
  | null
  | '' // 空字符串字面量
  | []; // 空数组字面量 (表示长度为0的数组)
/**
 * 判断值是否为空：
 * - undefined 或 null
 * - 空字符串（忽略空白字符）
 * - 空数组
 * - 空对象（无自有属性）
 *
 * @param value 要判断的值
 * @returns 如果为空返回 true，否则返回 false
 */
export function isEmpty(value: any): value is EmptyValue {
  return (
    value === undefined ||
    value === null ||
    (typeof value === 'string' && value.trim() === '') ||
    (Array.isArray(value) && value.length === 0) ||
    (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0)
  );
}

export function onConvertCheckBox(data: any[], { label, value }: { label: string; value: string }) {
  if (isEmpty(data)) return;

  return data.reduce((arr, obj) => {
    arr.push({ label: obj[label], value: obj[value] });
    return arr;
  }, []);
}

/**
 * 默认table 当前页面最后一行删除时，自动跳转到上一页
 * @param dataSource
 * @param actionRef
 */
export function onDataSourceChange(dataSource: any, actionRef: any) {
  if (!isEmpty(actionRef)) {
    const { current, pageSize } = actionRef.current?.pageInfo;
    if (parseInt(current) > 1 && isEmpty(dataSource)) {
      console.log('dataSource', { current: `${parseInt(current) - 1}`, pageSize });
      actionRef.current?.setPageInfo({
        ...actionRef.current.pageInfo,
        current: `${parseInt(current) - 1}`,
      });
      actionRef.current?.reload();
    }
  }
}

// 将 file 转为 Base64 的函数
export function fileToBase64(file: any) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    // 文件读取成功的回调
    reader.onload = function (e) {
      resolve(e.target?.result); // 读取成功时，返回 Base64 字符串
    };

    // 文件读取失败的回调
    reader.onerror = function (err) {
      reject(err); // 读取失败时，返回错误
    };

    reader.readAsDataURL(file); // 将文件读取为 Data URL (Base64)
  });
}

/**
 * 移出千分符号
 * @param numStr
 */
export const removeThousandSeparator = (numStr: string | number) => {
  console.log('numStr', numStr);
  if (typeof numStr !== 'string') {
    numStr = String(numStr); // 将非字符串转换为字符串
  }
  return numStr.replace(/,/g, '');
};

/**
 * 设置千分符号
 * @param num
 */
export const addThousandSeparator = (num: string) => {
  if (num === undefined || num === null) return ''; // 处理 undefined 或 null 的情况

  // 确保 num 是字符串类型
  const strNum = num.toString();

  // 允许数字和一个小数点，其它字符被移除
  const rawValue = strNum.replace(/[^0-9.]/g, '');

  // 确保只有一个小数点
  const [integer, decimal] = rawValue.split('.');

  // 限制最多两位小数
  const validDecimal = decimal ? decimal.slice(0, 2) : '';

  // 格式化整数部分为千分符
  const formattedInteger = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  // 返回格式化后的值，确保整数和小数部分正确拼接
  return validDecimal
    ? `${formattedInteger}.${validDecimal}`
    : `${formattedInteger}${rawValue.includes('.') ? '.' : ''}`;
};

/**
 * 计算含税金额、不含税金额、增值税
 * @param param0
 * @returns
 */
export function computeTaxIncludedExcludedAndTax({
  amount,
  taxRate,
  isTaxIncluded,
}: {
  amount: number;
  taxRate: number;
  isTaxIncluded: boolean;
}): { taxAmount: number; taxExcludedAmount: number; taxIncludedAmount: number } {
  const rate = math.divide(math.bignumber(taxRate), 100);
  let taxAmount: any, taxIncludedAmount: any, taxExcludedAmount: any;
  if (isTaxIncluded) {
    taxIncludedAmount = math.bignumber(amount);
    taxExcludedAmount = math.divide(taxIncludedAmount, math.add(rate, 1));
    taxAmount = math.subtract(taxIncludedAmount, taxExcludedAmount);
  } else {
    taxExcludedAmount = math.bignumber(amount);
    taxAmount = math.multiply(taxExcludedAmount, rate);
    taxIncludedAmount = math.add(taxExcludedAmount, taxAmount);
  }
  return {
    taxAmount: Number(math.format(taxAmount, { notation: 'fixed', precision: 2 })),
    taxIncludedAmount: Number(math.format(taxIncludedAmount, { notation: 'fixed', precision: 2 })),
    taxExcludedAmount: Number(math.format(taxExcludedAmount, { notation: 'fixed', precision: 2 })),
  };
}

/**
 * 处理表格行数据变化，根据含税金额、不含税金额、税率计算增值税、含税金额、不含税金额
 * @param action
 */
export function handelFormListChange(action: any) {
  const data = action.getCurrentRowData();
  const { tax_rate, is_include_tax } = data;
  const key = is_include_tax === 1 ? 'contract_amount' : 'exclud_tax_amount';
  const pattern = /[^.]$/;
  if (!isEmpty(data[key]) && !isEmpty(tax_rate) && pattern.test(data[key])) {
    const { taxAmount, taxExcludedAmount, taxIncludedAmount } = computeTaxIncludedExcludedAndTax({
      amount: parseFloat(data[key]),
      taxRate: tax_rate,
      isTaxIncluded: is_include_tax === 1,
    });
    action.setCurrentRowData({
      tax_rate,
      is_include_tax,
      exclud_tax_amount: taxExcludedAmount,
      contract_amount: taxIncludedAmount,
      tax_amount: taxAmount,
    });
  }
}

/**
 * 格式化时间，为当前时间的小时、分钟、秒
 * @param time 时间字符串
 * @returns
 */
export function formatTimeHourMinuteSecond(time: string) {
  const nowTime = dayjs();
  return dayjs(time)
    .hour(nowTime.hour())
    .minute(nowTime.minute())
    .second(nowTime.second())
    .format('YYYY-MM-DD HH:mm:ss');
}
